import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Shared primitives for the Momentum tips aesthetic.
 * Hairline cards, iris-accented headings, success-ticked bullet lists.
 */

// Section heading — General Sans 500, iris-accented icon by default.
export function SectionHeading({
  icon: Icon,
  children,
  iconClassName,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  iconClassName?: string;
}) {
  return (
    <h4 className="flex items-center gap-2 font-sans text-[13px] font-medium text-foreground">
      <Icon className={cn("h-3.5 w-3.5 text-accent", iconClassName)} />
      {children}
    </h4>
  );
}

// Hairline card — no gradient, no shadow.
export function HairlineCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border bg-card p-3",
        className
      )}
    >
      {children}
    </div>
  );
}

// Tip list — success tick + muted-foreground body.
export function TipList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((tip, index) => (
        <li
          key={index}
          className="flex gap-2 font-sans text-[13px] leading-[1.55] text-muted-foreground"
        >
          <Check
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success"
            strokeWidth={2.5}
          />
          <span>{tip}</span>
        </li>
      ))}
    </ul>
  );
}

// Row-style link button — full-width hairline card with arrow affordance.
export function LinkRowButton({
  onClick,
  children,
  trailing,
}: {
  onClick: () => void;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "group flex w-full items-center justify-between gap-2",
        "rounded-lg border border-border bg-card px-3 py-2 text-left",
        "font-sans text-[13px] font-normal text-foreground",
        "transition-colors duration-150 ease-out hover:bg-muted"
      )}
    >
      <span className="truncate">{children}</span>
      {trailing ?? (
        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground transition-transform duration-150 ease-out group-hover:translate-x-0.5" />
      )}
    </button>
  );
}
